import { useState } from 'react';
import { Monitor, Save, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AuthenticatedResponse, SessionUser } from '@nlinks/shared';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/store/auth';
import { formatDateTime, formatRelative } from '@/lib/format';
import { Alert, Badge, Button, Card, CardHeader, Field, Input, useToast } from '@/components/ui';

interface SessionRow {
  id: string;
  userAgent: string | null;
  ipAddress: string | null;
  lastUsedAt: string;
  createdAt: string;
  current: boolean;
}

/** Turns a raw UA string into something a person can recognise their device by. */
function describeDevice(userAgent: string | null): string {
  if (!userAgent) return 'Unknown device';
  const browser = /Edg\//.test(userAgent)
    ? 'Edge'
    : /Chrome\//.test(userAgent)
      ? 'Chrome'
      : /Safari\//.test(userAgent)
        ? 'Safari'
        : /Firefox\//.test(userAgent)
          ? 'Firefox'
          : 'Browser';
  const os = /Windows/.test(userAgent)
    ? 'Windows'
    : /Mac OS X|Macintosh/.test(userAgent)
      ? 'macOS'
      : /Android/.test(userAgent)
        ? 'Android'
        : /iPhone|iPad/.test(userAgent)
          ? 'iOS'
          : /Linux/.test(userAgent)
            ? 'Linux'
            : 'Unknown OS';
  return `${browser} on ${os}`;
}

function ProfileCard() {
  const { user, setUser } = useAuth();
  const { push } = useToast();
  const [fullName, setFullName] = useState(user?.fullName ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [company, setCompany] = useState(user?.company ?? '');

  const save = useMutation({
    mutationFn: () => api.patch<SessionUser>('/api/auth/me', { fullName, phone, company }),
    onSuccess: (updated) => {
      setUser(updated);
      push('success', 'Profile updated.');
    },
    onError: (err) => push('error', err instanceof ApiError ? err.message : 'Could not save your profile.'),
  });

  return (
    <Card>
      <CardHeader title="Profile" description="Your contact details." />
      <form
        className="space-y-4 p-5"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Full name" htmlFor="fullName" required>
            <Input id="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} required minLength={2} />
          </Field>
          <Field
            label="Email"
            htmlFor="email"
            hint="Sign-in codes go here. Only an administrator can change it."
          >
            <Input id="email" value={user?.email ?? ''} disabled />
          </Field>
          <Field label="Phone" htmlFor="phone">
            <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Optional" />
          </Field>
          <Field label="Company" htmlFor="company">
            <Input id="company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Optional" />
          </Field>
        </div>

        <div className="flex justify-end border-t border-surface-border pt-4">
          <Button type="submit" loading={save.isPending} icon={<Save className="h-4 w-4" />}>
            Save changes
          </Button>
        </div>
      </form>
    </Card>
  );
}

function PasswordCard() {
  const { signIn } = useAuth();
  const { push } = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: () =>
      api.post<AuthenticatedResponse>('/api/auth/change-password', { currentPassword, newPassword, confirmPassword }),
    onSuccess: (response) => {
      // Every other session was just revoked server-side; adopt the new pair
      // so this tab isn't logged out along with them.
      signIn(response);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError(null);
      push('success', 'Password updated. Other devices have been signed out.');
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not update your password.'),
  });

  return (
    <Card>
      <CardHeader title="Password" description="Changing this signs out every other device." />
      <form
        className="space-y-4 p-5"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          change.mutate();
        }}
      >
        {error ? <Alert severity="error">{error}</Alert> : null}

        <Field label="Current password" htmlFor="current" required>
          <Input
            id="current"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="New password" htmlFor="new" required hint="At least 12 characters, mixed case and a digit.">
            <Input
              id="new"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </Field>
          <Field
            label="Confirm new password"
            htmlFor="confirm"
            required
            error={confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : undefined}
          >
            <Input
              id="confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </Field>
        </div>

        <div className="flex justify-end border-t border-surface-border pt-4">
          <Button
            type="submit"
            loading={change.isPending}
            disabled={!currentPassword || !newPassword || newPassword !== confirmPassword}
          >
            Update password
          </Button>
        </div>
      </form>
    </Card>
  );
}

function SessionsCard() {
  const queryClient = useQueryClient();
  const { push } = useToast();

  const sessions = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.get<SessionRow[]>('/api/auth/sessions'),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/api/auth/sessions/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sessions'] });
      push('success', 'That device has been signed out.');
    },
  });

  return (
    <Card>
      <CardHeader title="Active sessions" description="Devices currently signed in to your account." />
      {sessions.isLoading ? (
        <div className="p-5">
          <div className="skeleton h-24 w-full" />
        </div>
      ) : (
        <ul className="divide-y divide-surface-border">
          {sessions.data?.map((session) => (
            <li key={session.id} className="flex flex-wrap items-center gap-3 px-5 py-4">
              <Monitor className="h-4 w-4 shrink-0 text-slate-600" aria-hidden />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-medium text-slate-200">{describeDevice(session.userAgent)}</p>
                  {session.current ? <Badge tone="success">This device</Badge> : null}
                </div>
                <p className="mt-0.5 text-xs text-slate-500">
                  {session.ipAddress ?? 'unknown IP'} · active {formatRelative(session.lastUsedAt)} · signed in{' '}
                  {formatDateTime(session.createdAt)}
                </p>
              </div>
              {!session.current ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => revoke.mutate(session.id)}
                  loading={revoke.isPending && revoke.variables === session.id}
                  icon={<Trash2 className="h-3.5 w-3.5" />}
                >
                  Sign out
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function SettingsPage() {
  const { user } = useAuth();

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-slate-100">Account settings</h1>
        <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
          Login ID <span className="font-mono text-brand-400">{user?.uid}</span>
          <Badge tone="brand">{user?.tier}</Badge>
        </p>
      </div>

      <ProfileCard />
      <PasswordCard />
      <SessionsCard />
    </div>
  );
}
